import React, { useEffect, useMemo } from 'react';
import { Form, Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { Col, Row } from 'reactstrap';
import PropTypes from 'prop-types';
import FormInputField from '../../../common/components/formFields/FormInputField';
import CommonButton from '../../../common/components/CommonButton';
import FormDatePickerField from '../../../common/components/formFields/FormDatePickerField';
import calendarIcon from '../../../assets/img/icons/calendar.svg';
import { CommonButtonVariants } from '../../../constants/CommonButtonVariants';
import FormSelectField from '../../../common/components/formFields/FormSelectField';
import { fetchOrganizationLocations } from '../../SettingsActions';

const ALL_LOCATIONS_OPTION = { value: null, label: 'All' };

const initialValues = {
  invoiceNumber: '',
  invoiceDateFrom: '',
  invoiceDateTo: '',
  location: ALL_LOCATIONS_OPTION,
};

function InvoiceForm({ handleSearch }) {
  const dispatch = useDispatch();
  const organizationLocations = useSelector((state) =>
    state.myOrganization.get('organizationLocations'),
  );

  useEffect(() => {
    dispatch(fetchOrganizationLocations());
  }, [dispatch]);

  const hasSingleLocation =
    Array.isArray(organizationLocations) && organizationLocations.length === 1;

  const locationOptions = useMemo(() => {
    const options = (Array.isArray(organizationLocations) ? organizationLocations : []).map(
      (loc) => ({
        value: loc.id,
        label: loc.name || loc.address || loc?.postalCode?.postcode,
      }),
    );
    return hasSingleLocation ? options : [ALL_LOCATIONS_OPTION, ...options];
  }, [organizationLocations, hasSingleLocation]);

  const selectedLocation = hasSingleLocation ? locationOptions[0] : ALL_LOCATIONS_OPTION;

  const initialFormValues = useMemo(
    () => ({ ...initialValues, location: selectedLocation }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedLocation.value, selectedLocation.label],
  );

  return (
    <Formik
      enableReinitialize
      initialValues={initialFormValues}
      onSubmit={handleSearch}
      validateOnBlur={true}
    >
      {() => (
        <Form noValidate>
          <Row className="d-flex align-items-center">
            <Col md={12}>
              <Row className="d-flex justify-content-between align-items-end">
                <Col md={3}>
                  <FormSelectField
                    name="location"
                    label="Location"
                    options={locationOptions}
                    disabled={hasSingleLocation}
                    labelVariant="bordered"
                  />
                </Col>
                <Col md={3}>
                  <FormInputField
                    name="invoiceNumber"
                    label="Number"
                    labelVariant="bordered"
                  />
                </Col>
                <Col md={3}>
                  <FormDatePickerField
                    name="invoiceDateFrom"
                    label="Date - From"
                    placeholder="DD/MM/YYYY"
                    icon={calendarIcon}
                    iconClassName="calendar-icon"
                    labelVariant="bordered"
                    closeBtn
                  />
                </Col>
                <Col md={3}>
                  <FormDatePickerField
                    name="invoiceDateTo"
                    label="Date - To"
                    placeholder="DD/MM/YYYY"
                    icon={calendarIcon}
                    iconClassName="calendar-icon"
                    labelVariant="bordered"
                    closeBtn
                  />
                </Col>
                <Col md={3} className="d-flex align-items-center">
                  <CommonButton
                    className="invoice-search-button mb-3"
                    type="submit"
                    label="Search"
                    variant={CommonButtonVariants.OUTLINED_RED}
                  />
                </Col>
              </Row>
            </Col>
          </Row>
        </Form>
      )}
    </Formik>
  );
}

InvoiceForm.propTypes = {
  handleSearch: PropTypes.func.isRequired,
};

export default InvoiceForm;
